/**
 * Sonderfaehigkeiten der Creeps (Masterprompt Abschnitte 66-68, 109).
 *
 * Der Masterprompt benennt Heiler, Anfuehrer, Beschwoerer und den Splitter, legt
 * aber nicht fest, wie sie genau wirken. Die folgenden Regeln hat der Product
 * Owner am 2026-09-22 entschieden:
 *
 * - Heiler: Flaechenheilung. Alle 2 Sekunden heilen sie alle ANDEREN
 *   verbuendeten Creeps im Umkreis um einen Anteil ihrer max. HP.
 * - Anfuehrer: mehr Schaden fuer verbuendete Creeps im Umkreis, der Warlord
 *   zusaetzlich mehr Ruestung.
 * - Beschwoerer: rufen regelmaessig Helfer, auch waehrend des Laufs.
 * - Splitter: zerfaellt beim Tod in zwei kleine Splitter.
 *
 * Die Art der Wirkung ist damit festgelegt. Die ZAHLEN sind TUNING_PLACEHOLDER
 * und duerfen nach Tests angepasst werden.
 */

import type { CreepDefinition } from './creep-types.js';

/** Flaechenheilung eines Heilers. */
export interface HealAbility {
  /** Abstand zwischen zwei Heilungen, in Sekunden. */
  intervalSeconds: number;
  /** Umkreis, in dem geheilt wird. */
  radius: number;
  /** Anteil der max. HP des Geheilten, der pro Heilung zurueckkommt. */
  healRatio: number;
}

/** Aura eines Anfuehrers. Mehrere Auren addieren sich NICHT - die staerkste zaehlt. */
export interface AuraAbility {
  radius: number;
  /** Zusaetzlicher Schaden als Anteil, 0.15 = +15 %. */
  damageBonus: number;
  /** Zusaetzliche Ruestung. */
  armorBonus: number;
}

/** Beschwoerung. */
export interface SummonAbility {
  intervalSeconds: number;
  /** Wie viele Helfer pro Beschwoerung erscheinen. */
  count: number;
  /** Hoechstens so viele Helfer DIESES Beschwoerers leben gleichzeitig. */
  maxAlive: number;
  /** ID des beschworenen Helfers. */
  summonId: string;
}

/** Teilung beim Tod. */
export interface SplitAbility {
  count: number;
  /** ID der Nachkommen. */
  childId: string;
}

/*
 * Zu den Radien: Die Reihen einer Welle laufen rund 8 Einheiten hintereinander
 * (Formation, siehe match.ts). Ein Heiler oder Anfuehrer soll mindestens die
 * Nachbarreihe erreichen - sonst wirkte er praktisch nur auf seine eigene Reihe.
 */

/*
 * HALBIERT am 09.10.2026 (Produktentscheidung).
 *
 * Gemessen ueber vier Laeufe zu je 288 Matches, beide Startwerte gleich:
 *
 *   Heiler schenken dazu   129 Prozent des erschienenen Wellenlebens
 *   Anteil an den Creeps     5 Prozent
 *   Welle kommt bis         57 Prozent des Weges, dann stirbt sie
 *
 * Ein Zwanzigstel der Einheiten verdoppelte also das Leben aller
 * anderen. Beide Wellen hielten sich dadurch in der Mitte fest, und das
 * Spiel fand keinen Weg zu enden.
 *
 * Vorher: medic 0.04, void_priest 0.06.
 */
export const HEAL_ABILITIES: Readonly<Record<string, HealAbility>> = {
  medic: { intervalSeconds: 2, radius: 12, healRatio: 0.02 },
  void_priest: { intervalSeconds: 2, radius: 13, healRatio: 0.03 },
};

export const AURA_ABILITIES: Readonly<Record<string, AuraAbility>> = {
  commander: { radius: 12, damageBonus: 0.15, armorBonus: 0 },
  warlord: { radius: 14, damageBonus: 0.25, armorBonus: 10 },
};

export const SUMMON_ABILITIES: Readonly<Record<string, SummonAbility>> = {
  necromancer: { intervalSeconds: 6, count: 1, maxAlive: 3, summonId: 'skeleton' },
  broodmother: { intervalSeconds: 5, count: 2, maxAlive: 6, summonId: 'broodling' },
};

export const SPLIT_ABILITIES: Readonly<Record<string, SplitAbility>> = {
  splitter: { count: 2, childId: 'shard' },
};

/**
 * Einheiten, die nur durch Faehigkeiten entstehen.
 *
 * Sie lassen sich NICHT kaufen: Sie stehen bewusst nicht in CREEPS und sind
 * deshalb fuer den Army Composer und die Pruefung beim SEND unbekannt. Sie
 * kosten nichts, bringen kein Income, belegen keine Army Points - und geben
 * dem Verteidiger kein Kill Gold. Sonst wuerde ein Necromancer dem Gegner
 * laufend Gold schenken.
 *
 * Portalschaden: laut Masterprompt 1 fuer die Adds der Broodmother. Skelette
 * und Splitter werden gleich behandelt.
 */
export const SPAWNED_CREEPS: readonly CreepDefinition[] = [
  {
    id: 'skeleton',
    name: 'Skeleton',
    tier: 3,
    cost: 0,
    incomeGain: 0,
    armyPoints: 0,
    portalDamage: 1,
    maxHp: 220,
    damage: 14,
    attackSpeed: 1.0,
    moveSpeed: 8,
    armor: 0,
    magicResistance: 0,
    aggroRange: 11,
    leashRange: 22,
    attackRange: 2,
    tags: ['melee'],
    specialBehaviour: 'none',
  },
  {
    id: 'broodling',
    name: 'Broodling',
    tier: 4,
    cost: 0,
    incomeGain: 0,
    armyPoints: 0,
    portalDamage: 1,
    maxHp: 320,
    damage: 18,
    attackSpeed: 1.2,
    moveSpeed: 9.5,
    armor: 2,
    magicResistance: 0,
    aggroRange: 11,
    leashRange: 22,
    attackRange: 2,
    tags: ['melee', 'fast'],
    specialBehaviour: 'none',
  },
  {
    // 35 % der HP eines Splitters (950).
    id: 'shard',
    name: 'Shard',
    tier: 3,
    cost: 0,
    incomeGain: 0,
    armyPoints: 0,
    portalDamage: 1,
    maxHp: 333,
    damage: 16,
    attackSpeed: 1.0,
    moveSpeed: 8.5,
    armor: 1,
    magicResistance: 0,
    aggroRange: 12,
    leashRange: 24,
    attackRange: 2,
    tags: ['melee'],
    specialBehaviour: 'none',
  },
];

const SPAWNED_BY_ID: ReadonlyMap<string, CreepDefinition> = new Map(
  SPAWNED_CREEPS.map((creep) => [creep.id, creep]),
);

/** Ist das eine Einheit, die nur durch eine Faehigkeit entsteht? */
export function isSpawnedCreep(id: string): boolean {
  return SPAWNED_BY_ID.has(id);
}

/** Beschworene Einheiten und Nachkommen - fuer den Server und die Darstellung. */
export function getSpawnedCreep(id: string): CreepDefinition | undefined {
  return SPAWNED_BY_ID.get(id);
}
